/** Real client for the AI-mode routes (api/app/ai/aimode_routes.py). Same origin + session cookie as ../http.ts. */
import { ApiError } from '../client'
import { reportUnauthorized } from '../http'
import type { ConversationSummary } from '../types'
import type { AiModeApi, AiModeView } from './types'

export class HttpAiModeApi implements AiModeApi {
  constructor(
    private readonly base: string,
    private readonly timeoutMs = 15000,
    private readonly fetchImpl: typeof fetch = (...a) => fetch(...a),
  ) {}

  private async req<T>(method: string, path: string, body?: unknown): Promise<T> {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), this.timeoutMs)
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    const url = new URL(`${this.base.replace(/\/+$/, '')}/api/v1${path}`, window.location.origin).toString()
    try {
      const res = await this.fetchImpl(url, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        credentials: 'same-origin',
        signal: ctrl.signal,
      })
      if (res.status === 401) reportUnauthorized()
      if (!res.ok) {
        let detail = res.statusText
        try {
          const j = (await res.json()) as { detail?: unknown }
          if (typeof j.detail === 'string') detail = j.detail
        } catch {
          /* body not JSON */
        }
        throw new ApiError(res.status, detail || `HTTP ${res.status}`)
      }
      return (await res.json()) as T
    } catch (e) {
      if (e instanceof ApiError) throw e
      if (e instanceof DOMException && e.name === 'AbortError') throw new ApiError(0, 'หมดเวลารอเซิร์ฟเวอร์')
      throw new ApiError(0, 'ต่อเซิร์ฟเวอร์ไม่ได้')
    } finally {
      clearTimeout(timer)
    }
  }

  private conv(id: string): string {
    return `/conversations/${encodeURIComponent(id)}`
  }

  view(convId: string): Promise<AiModeView> {
    return this.req('GET', `${this.conv(convId)}/ai-mode`)
  }

  set(convId: string, on: boolean): Promise<ConversationSummary> {
    return this.req('PUT', `${this.conv(convId)}/ai-mode`, { on })
  }

  acceptHandoff(convId: string): Promise<ConversationSummary> {
    return this.req('POST', `${this.conv(convId)}/ai-handoff/accept`)
  }
}
